import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import {
  FiAlertTriangle,
  FiBell,
  FiCheck,
  FiCheckCircle,
  FiMoreHorizontal,
  FiMoreVertical,
  FiRefreshCw,
  FiTrash2,
} from "react-icons/fi";
import { showToast } from "../../components/common/notification/Notification";
import { getAuthUser, getUserTypeName } from "../../admin/navigation/adminNavigation";
import { usePropertyApiClient } from "../property/services/usePropertyApiClient";
import { createNotificationService, NOTIFICATION_PAGE_SIZE } from "./services/notificationService";
import { adjustUnreadCount, setUnreadCount, useUnreadCount } from "./notificationUnreadStore";
import {
  FILTER_GROUPS,
  formatFullDate,
  formatRelativeTime,
  getGroupLabel,
  getNotificationType,
  parseCreatedOn,
  resolveNotificationTarget,
} from "./notificationConfig";
import "../../components/common/formkit/formkit.css";
import "./NotificationsPage.css";

const GROUP_ORDER = ["Today", "Yesterday", "Earlier"];
const MARK_ALL_BATCH = 5;
const MARK_ALL_MAX_PAGES = 25;

const byNewest = (a, b) => {
  const ta = parseCreatedOn(a.created_on)?.getTime() || 0;
  const tb = parseCreatedOn(b.created_on)?.getTime() || 0;
  return tb !== ta ? tb - ta : (Number(b.id) || 0) - (Number(a.id) || 0);
};

/** One row. The row opens the target; the "..." menu is a separate control. */
const NotificationRow = ({ notification, role, onOpen, onMarkRead, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const type = getNotificationType(notification);
  const Icon = type.icon;
  const isUnread = !notification.is_read;
  const date = parseCreatedOn(notification.created_on);
  const target = resolveNotificationTarget(notification, role);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "mousedown" && menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  const open = () => onOpen(notification, target);

  return (
    <li className={`nt-row${isUnread ? " nt-row--unread" : ""}${target ? " nt-row--link" : ""}`}>
      <div
        className="nt-row__main"
        role={target ? "link" : undefined}
        tabIndex={0}
        onClick={open}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter") {
            event.preventDefault();
            open();
          }
        }}
        aria-label={`${isUnread ? "Unread. " : ""}${notification.notification_title || type.label}`}
      >
        <span className={`nt-icon nt-icon--${type.tone}`} aria-hidden="true">
          <Icon size={18} />
        </span>
        <div className="nt-row__body">
          <div className="nt-row__top">
            <p className="nt-row__title">{notification.notification_title}</p>
            <time className="nt-row__time" dateTime={date ? date.toISOString() : undefined} title={formatFullDate(date)}>
              {formatRelativeTime(date)}
            </time>
          </div>
          <p className="nt-row__text">{notification.notification_text}</p>
          <span className="nt-row__tag">{type.label}</span>
        </div>
      </div>

      <div className="nt-row__side" ref={menuRef}>
        {isUnread && <span className="nt-dot" role="img" aria-label="Unread" />}
        <button
          type="button"
          className="nt-iconbtn"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <FiMoreVertical size={18} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div className="nt-menu" role="menu">
            {isUnread && (
              <button
                type="button"
                role="menuitem"
                className="nt-menu__item"
                onClick={() => {
                  setMenuOpen(false);
                  onMarkRead(notification);
                }}
              >
                <FiCheck size={15} aria-hidden="true" /> Mark as read
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="nt-menu__item nt-menu__item--danger"
              onClick={() => {
                setMenuOpen(false);
                onDelete(notification);
              }}
            >
              <FiTrash2 size={15} aria-hidden="true" /> Delete
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

const SkeletonRows = () => (
  <ul className="nt-list" aria-hidden="true">
    {Array.from({ length: 6 }).map((_, i) => (
      <li key={i} className="nt-row nt-row--skeleton">
        <span className="fk-skel nt-skel-icon" />
        <div className="nt-skel-lines">
          <span className="fk-skel nt-skel-line nt-skel-line--short" />
          <span className="fk-skel nt-skel-line" />
        </div>
      </li>
    ))}
  </ul>
);

/** /notification - the logged-in user's notifications (same endpoints as the mobile screen) */
const NotificationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authState = useSelector((state) => state.auth);
  const userId = getAuthUser(authState)?.id;
  const role = String(getUserTypeName(authState) || "").toUpperCase();
  const apiClient = usePropertyApiClient();
  const service = useMemo(() => createNotificationService(apiClient), [apiClient]);
  const unreadCount = useUnreadCount();

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [announce, setAnnounce] = useState("");

  const filter = searchParams.get("filter") || "all";
  const setFilter = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("filter");
    else params.set("filter", next);
    setSearchParams(params);
  };

  const load = useCallback(async () => {
    if (!userId) return;
    setStatus("loading");
    try {
      const [{ notifications }, count] = await Promise.all([
        service.list(userId, { page: 1 }),
        service.getUnreadCount().catch(() => null),
      ]);
      setItems([...notifications].sort(byNewest));
      setPage(1);
      setHasMore(notifications.length === NOTIFICATION_PAGE_SIZE);
      if (count !== null) setUnreadCount(count);
      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  }, [service, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { notifications } = await service.list(userId, { page: page + 1 });
      setItems((prev) => {
        const known = new Set(prev.map((n) => n.id));
        return [...prev, ...notifications.filter((n) => !known.has(n.id))].sort(byNewest);
      });
      setPage((p) => p + 1);
      setHasMore(notifications.length === NOTIFICATION_PAGE_SIZE);
    } catch (error) {
      showToast("Could not load more notifications", { type: "error" });
    } finally {
      setLoadingMore(false);
    }
  };

  const patchItem = (id, patch) =>
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));

  /** Optimistic: flip the row and the bell, roll both back if the request fails. */
  const markRead = async (notification) => {
    if (notification.is_read) return true;
    patchItem(notification.id, { is_read: true });
    adjustUnreadCount(-1);
    try {
      await service.setRead(notification.id, true);
      return true;
    } catch (error) {
      patchItem(notification.id, { is_read: false });
      adjustUnreadCount(1);
      showToast("Could not mark the notification as read", { type: "error" });
      return false;
    }
  };

  const handleOpen = async (notification, target) => {
    markRead(notification);
    if (target) navigate(target);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const victim = toDelete;
    setDeleting(true);
    const snapshot = items;
    setItems((prev) => prev.filter((n) => n.id !== victim.id));
    if (!victim.is_read) adjustUnreadCount(-1);
    try {
      await service.remove(victim.id);
      showToast("Notification deleted", { type: "success" });
      setAnnounce("Notification deleted");
    } catch (error) {
      setItems(snapshot);
      if (!victim.is_read) adjustUnreadCount(1);
      showToast("Could not delete the notification", { type: "error" });
    } finally {
      setDeleting(false);
      setToDelete(null);
    }
  };

  /**
   * There is no bulk endpoint (the mobile screen loops PUT /notify/setRead per notification), so
   * this does the same in small batches, after loading the remaining pages so nothing is missed.
   */
  const markAllRead = async () => {
    setMarkingAll(true);
    setActionsOpen(false);
    let all = items;
    try {
      let current = page;
      let more = hasMore;
      while (more && current < MARK_ALL_MAX_PAGES) {
        const { notifications } = await service.list(userId, { page: current + 1 });
        current += 1;
        more = notifications.length === NOTIFICATION_PAGE_SIZE;
        const known = new Set(all.map((n) => n.id));
        all = [...all, ...notifications.filter((n) => !known.has(n.id))];
      }
      setPage(current);
      setHasMore(more);

      const unread = all.filter((n) => !n.is_read);
      const failed = new Set();
      for (let i = 0; i < unread.length; i += MARK_ALL_BATCH) {
        const batch = unread.slice(i, i + MARK_ALL_BATCH);
        const results = await Promise.allSettled(batch.map((n) => service.setRead(n.id, true)));
        results.forEach((r, idx) => r.status === "rejected" && failed.add(batch[idx].id));
      }
      setItems(all.map((n) => (!n.is_read && !failed.has(n.id) ? { ...n, is_read: true } : n)).sort(byNewest));
      service.getUnreadCount().then(setUnreadCount).catch(() => {});
      if (failed.size) {
        showToast(`${failed.size} notification(s) could not be marked as read`, { type: "error" });
      } else {
        showToast("All notifications marked as read", { type: "success" });
        setAnnounce("All notifications marked as read");
      }
    } catch (error) {
      setItems(all);
      showToast("Could not mark all notifications as read", { type: "error" });
    } finally {
      setMarkingAll(false);
    }
  };

  const presentGroups = useMemo(() => {
    const keys = new Set(items.map((n) => getNotificationType(n).group));
    return FILTER_GROUPS.filter((g) => keys.has(g.key));
  }, [items]);

  const visible = useMemo(() => {
    if (filter === "unread") return items.filter((n) => !n.is_read);
    if (filter === "all") return items;
    return items.filter((n) => getNotificationType(n).group === filter);
  }, [items, filter]);

  const grouped = useMemo(() => {
    const now = new Date();
    const map = new Map(GROUP_ORDER.map((label) => [label, []]));
    visible.forEach((n) => map.get(getGroupLabel(parseCreatedOn(n.created_on), now)).push(n));
    return GROUP_ORDER.map((label) => ({ label, rows: map.get(label) })).filter((g) => g.rows.length);
  }, [visible]);

  const homePath = role === "PARTNER" ? "/partner/workspace" : "/customer/workspace";
  const hasUnreadLoaded = items.some((n) => !n.is_read);
  const filters = [
    { key: "all", label: "All" },
    { key: "unread", label: "Unread", badge: unreadCount },
    ...presentGroups,
  ];

  const emptyUnread = filter === "unread";

  return (
    <div className="nt-page">
      <header className="nt-header">
        <div className="nt-header__lead">
          <span className="nt-header__icon" aria-hidden="true">
            <FiBell size={22} />
          </span>
          <div>
            <h2 className="nt-header__title">Notifications</h2>
            <p className="nt-header__subtitle">Stay up to date with your activity</p>
          </div>
        </div>

        {status === "success" && items.length > 0 && (
          <div className="nt-header__actions">
            <button
              type="button"
              className="fk-btn fk-btn--ghost nt-actions-desktop"
              onClick={markAllRead}
              disabled={markingAll || (!hasUnreadLoaded && unreadCount === 0)}
            >
              <FiCheckCircle size={16} aria-hidden="true" />
              {markingAll ? "Marking..." : "Mark all as read"}
            </button>
            <div className="nt-actions-mobile">
              <button
                type="button"
                className="nt-iconbtn nt-iconbtn--bordered"
                aria-label="Page actions"
                aria-haspopup="menu"
                aria-expanded={actionsOpen}
                onClick={() => setActionsOpen((v) => !v)}
              >
                <FiMoreHorizontal size={18} aria-hidden="true" />
              </button>
              {actionsOpen && (
                <div className="nt-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="nt-menu__item"
                    onClick={markAllRead}
                    disabled={markingAll || (!hasUnreadLoaded && unreadCount === 0)}
                  >
                    <FiCheckCircle size={15} aria-hidden="true" /> Mark all as read
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {status !== "error" && (
        <div className="nt-filters" role="tablist" aria-label="Filter notifications">
          {filters.map(({ key, label, badge }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              className={`nt-filter${filter === key ? " nt-filter--active" : ""}`}
              onClick={() => setFilter(key)}
            >
              {label}
              {badge > 0 && <span className="nt-filter__badge">{badge > 99 ? "99+" : badge}</span>}
            </button>
          ))}
        </div>
      )}

      <section className="nt-card" aria-label="Notification list" aria-busy={status === "loading"}>
        {status === "loading" && <SkeletonRows />}

        {status === "error" && (
          <div className="nt-state" role="alert">
            <span className="nt-state__icon nt-state__icon--error" aria-hidden="true">
              <FiAlertTriangle size={30} />
            </span>
            <h3 className="nt-state__title">We could not load your notifications</h3>
            <p className="nt-state__text">Check your connection and try again.</p>
            <button type="button" className="fk-btn fk-btn--primary" onClick={load}>
              <FiRefreshCw size={15} aria-hidden="true" /> Retry
            </button>
          </div>
        )}

        {status === "success" && visible.length === 0 && (
          <div className="nt-state">
            <span className="nt-state__icon" aria-hidden="true">
              <FiBell size={30} />
            </span>
            <h3 className="nt-state__title">{emptyUnread ? "No unread notifications" : "You're all caught up"}</h3>
            <p className="nt-state__text">
              {emptyUnread || filter !== "all" ? "Nothing here right now." : "New notifications will appear here."}
            </p>
            {filter !== "all" ? (
              <button type="button" className="fk-btn fk-btn--ghost" onClick={() => setFilter("all")}>
                Show all notifications
              </button>
            ) : (
              <Link to={homePath} className="fk-btn fk-btn--primary nt-link-btn">
                Back to Overview
              </Link>
            )}
          </div>
        )}

        {status === "success" &&
          grouped.map(({ label, rows }) => (
            <div key={label} className="nt-group">
              <h3 className="nt-group__label">{label}</h3>
              <ul className="nt-list">
                {rows.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    role={role}
                    onOpen={handleOpen}
                    onMarkRead={markRead}
                    onDelete={setToDelete}
                  />
                ))}
              </ul>
            </div>
          ))}

        {status === "success" && hasMore && visible.length > 0 && (
          <div className="nt-more">
            <button type="button" className="fk-btn fk-btn--ghost" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          </div>
        )}
      </section>

      <div className="nt-sr" role="status" aria-live="polite">
        {announce}
      </div>

      <Modal show={!!toDelete} onHide={() => !deleting && setToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Delete notification?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This notification will be removed permanently.</Modal.Body>
        <Modal.Footer>
          <button type="button" className="fk-btn fk-btn--ghost" onClick={() => setToDelete(null)} disabled={deleting}>
            Cancel
          </button>
          <button type="button" className="fk-btn nt-btn-danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NotificationsPage;
